// The one door every board widget uses to reach data.
//
// On a real board these are Firestore's own functions and the network,
// unchanged. In preview mode (the admin dashboard's Station Board Studio
// showing a template before anyone builds it) they are served from an
// in-memory fake agency instead, so the Studio can show the real widgets —
// the exact components a TV runs — filled with believable data, without a
// login and without touching any real agency's records.
//
// Widgets import from here rather than from "firebase/firestore" / "fetch" /
// "firebase/functions" directly; that is the whole seam.

import * as fs from "firebase/firestore";
import axios from "axios";
import { httpsCallable } from "firebase/functions";
import { ref as rtdbRef, onValue } from "firebase/database";
import { db as realDb, functions, database } from "../firebase";
import { loadHydrantsForArea } from "../utils/hydrantPoiLoader";

let demo = null; // { collections: { "path": [{ id, ...data }] }, docs: { "path": data }, fetch(url), call(name, data) }

export function enableDemoData(fixtures) {
  demo = fixtures;
}
export function isDemoData() {
  return demo !== null;
}

// Screenshot capture asks for a "still" preview: animations that would make a
// capture depend on timing (the radar loop) hold on their final frame.
let still = false;
export function setPreviewStill(value) {
  still = !!value;
}
export function isPreviewStill() {
  return still;
}

// Screenshot capture can ask the legacy board's dashboard to open on a given
// view (memos, duty crew, ...) instead of starting its rotation at memos.
let legacyView = null;
export function setPreviewLegacyView(view) {
  legacyView = view || null;
}
export function previewLegacyView() {
  return demo ? legacyView : null;
}

export const db = realDb;

// Preview only: fake reflectivity (a GeoJSON FeatureCollection) for the storm
// map, `minutesAgo` minutes back. Null on a real board, which uses live radar.
export function demoRadar(minutesAgo) {
  return demo && demo.radar ? demo.radar(minutesAgo) : null;
}

// ---- refs -------------------------------------------------------------------

const joinPath = (segments) => segments.map(String).join("/");

export function collection(dbArg, ...segments) {
  if (!demo) return fs.collection(dbArg, ...segments);
  return { __demo: "collection", path: joinPath(segments), constraints: [] };
}

export function doc(dbArg, ...segments) {
  if (!demo) return fs.doc(dbArg, ...segments);
  return { __demo: "doc", path: joinPath(segments) };
}

export function where(field, op, value) {
  if (!demo) return fs.where(field, op, value);
  return { field, op, value };
}

export function orderBy(field, direction = "asc") {
  if (!demo) return fs.orderBy(field, direction);
  return { orderBy: field, direction };
}

export function query(ref, ...constraints) {
  if (!demo) return fs.query(ref, ...constraints);
  return { ...ref, constraints: [...(ref.constraints || []), ...constraints] };
}

// ---- reads ------------------------------------------------------------------

const sortValue = (v) => (v && typeof v.toMillis === "function" ? v.toMillis() : v instanceof Date ? v.getTime() : v);

function matches(row, { field, op, value: raw }) {
  // Timestamps compare by time, like Firestore.
  const v = sortValue(row[field]);
  const value = sortValue(raw);
  switch (op) {
    case "==": return v === value;
    case "!=": return v !== value;
    case "in": return Array.isArray(value) && value.includes(v);
    case "array-contains": return Array.isArray(v) && v.includes(value);
    case ">": return v > value;
    case ">=": return v >= value;
    case "<": return v < value;
    case "<=": return v <= value;
    default: return true;
  }
}

function demoQuerySnapshot(ref) {
  const constraints = ref.constraints || [];
  const rows = (demo.collections[ref.path] || []).filter((row) =>
    constraints.every((c) => c.orderBy || matches(row, c))
  );
  constraints
    .filter((c) => c.orderBy)
    .reverse()
    .forEach(({ orderBy: field, direction }) => {
      const sign = direction === "desc" ? -1 : 1;
      rows.sort((a, b) => {
        const av = sortValue(a[field]);
        const bv = sortValue(b[field]);
        return av === bv ? 0 : av > bv ? sign : -sign;
      });
    });
  const docs = rows.map(({ id, ...data }) => ({ id, data: () => data, exists: () => true }));
  return {
    docs,
    size: docs.length,
    empty: docs.length === 0,
    forEach: (fn) => docs.forEach(fn),
    // A preview's data never changes, so every document arrives as "added".
    docChanges: () => docs.map((d, newIndex) => ({ type: "added", doc: d, oldIndex: -1, newIndex })),
  };
}

function demoDocSnapshot(ref) {
  const data = demo.docs[ref.path];
  const id = ref.path.split("/").pop();
  return { id, exists: () => data !== undefined, data: () => data };
}

function demoSnapshot(ref) {
  return ref.__demo === "doc" ? demoDocSnapshot(ref) : demoQuerySnapshot(ref);
}

export function onSnapshot(ref, onNext, onError) {
  if (!demo) return fs.onSnapshot(ref, onNext, onError);
  // Asynchronous like the real listener, so widgets see the same render order.
  const t = setTimeout(() => onNext(demoSnapshot(ref)), 0);
  return () => clearTimeout(t);
}

export async function getDoc(ref) {
  if (!demo) return fs.getDoc(ref);
  return demoDocSnapshot(ref);
}

export async function getDocs(ref) {
  if (!demo) return fs.getDocs(ref);
  return demoQuerySnapshot(ref);
}

// ---- network and callables ----------------------------------------------------

// fetch() for third-party feeds (weather, NWS alerts). Returns a Response-like
// object in preview mode.
export async function boardFetch(url, options) {
  if (!demo) return fetch(url, options);
  const body = demo.fetch ? demo.fetch(String(url)) : undefined;
  return {
    ok: body !== undefined,
    status: body !== undefined ? 200 : 404,
    json: async () => body ?? {},
  };
}

// axios.get(url) for the legacy board's feeds: resolves to { data } like axios.
export async function boardGet(url) {
  if (!demo) return axios.get(url);
  const body = demo.fetch ? demo.fetch(String(url)) : undefined;
  if (body === undefined) throw new Error(`No demo data for ${url}`);
  return { data: body };
}

// httpsCallable(functions, name)(data), resolved to { data } like the SDK.
export async function callFunction(name, data) {
  if (!demo) return httpsCallable(functions, name)(data);
  if (!demo.call) throw new Error(`No demo data for ${name}`);
  return { data: await demo.call(name, data) };
}

// ---- realtime database and hydrants ------------------------------------------

// Subscribe to a Realtime Database path (e.g. /UnitLocation/{agency}); the
// callback gets the value (or null). Returns an unsubscribe function.
export function watchRealtime(path, onValueChange) {
  if (!demo) return onValue(rtdbRef(database, path), (snap) => onValueChange(snap.val()));
  const t = setTimeout(() => onValueChange(demo.realtime ? demo.realtime(path) ?? null : null), 0);
  return () => clearTimeout(t);
}

// Hydrants around an area, same shape as utils/hydrantPoiLoader's result.
export async function loadHydrants(area, options) {
  if (!demo) return loadHydrantsForArea(area, options);
  return { pois: (demo.hydrants ? demo.hydrants(area) : []).map((poi) => ({ poi })) };
}
